import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { BackToTopButton } from '@/components/BackToTopButton';
// R1-2: Removed duplicate NavigationButtons import
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PropertyDetailSkeleton } from '@/components/LoadingSkeleton';
import { ReviewsList } from '@/components/ReviewsList';
import { AddReviewForm } from '@/components/AddReviewForm';
import PhotoGallery from '@/components/PhotoGallery';
import SimilarProperties from '@/components/SimilarProperties';
import PropertyLocationMap from '@/components/PropertyLocationMap';
import SEOHead, { generateSchema } from '@/components/SEOHead';
import { motion } from 'framer-motion';
import { Home, Search, ArrowRight } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Star, MapPin, Users, Bed, Bath, Waves, Mail, MessageCircle, Share2, Heart, Globe, Facebook, Instagram, ExternalLink, Twitter, Copy, Link, Wifi, Car, Snowflake, UtensilsCrossed, Tv, WashingMachine, Wind, Dumbbell, Flame, Gamepad2, Dog, Accessibility, Zap, ChefHat } from 'lucide-react';
import { api, propertiesAPI, contactAPI, reviewsAPI } from '@/lib/api';
import { storage } from '@/lib/storage';
import { analytics } from '@/lib/analytics';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useTextTranslation } from '@/hooks/usePropertyTranslation';
import { usePropertyTitle } from '@/hooks/usePageTitle';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const language = i18n.language === 'pt-BR' || i18n.language === 'pt' ? 'pt' : 'en';
  const [property, setProperty] = useState(null);
  
  // Set page title dynamically based on property
  usePropertyTitle(property);
  
  const [reviews, setReviews] = useState([]);
  const [reviewsData, setReviewsData] = useState(null); // R1-1 FIX: Store full reviews response
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);
  
  // Translate property content using AI
  const { translatedText: translatedTitle, isLoading: titleLoading } = useTextTranslation(property?.title);
  const { translatedText: translatedDescription, isLoading: descLoading } = useTextTranslation(property?.description);
  const user = storage.getUser();

  // Translations
  const texts = {
    pt: {
      errorLoading: 'Erro ao carregar imóvel',
      notFound: 'Imóvel não encontrado',
      bedrooms: 'Quartos',
      bathrooms: 'Banheiros',
      guests: 'Hóspedes',
      perNight: '/noite',
      perNightSuggested: 'por noite (sugerido)',
      description: 'Descrição',
      amenities: 'Comodidades',
      location: 'Localização',
      reviews: 'Avaliações',
      contactHost: 'Contatar Anfitrião',
      sendEmail: 'Enviar Email',
      whatsapp: 'WhatsApp',
      share: 'Compartilhar',
      shareWhatsApp: 'Compartilhar no WhatsApp',
      shareInstagram: 'Copiar para Instagram',
      shareFacebook: 'Compartilhar no Facebook',
      shareTwitter: 'Compartilhar no Twitter',
      shareCopied: 'Link copiado para compartilhar!',
      addToFavorites: 'Adicionar aos Favoritos',
      removeFromFavorites: 'Remover dos Favoritos',
      writeReview: 'Escrever Avaliação',
      loginToReview: 'Faça login para avaliar',
      noReviews: 'Nenhuma avaliação ainda',
      beFirst: 'Seja o primeiro a avaliar!',
      hostLinks: 'Links do Anfitrião',
      website: 'Site',
      pool: 'Piscina',
      wifi: 'Wi-Fi',
      // R1-4: Property type translations
      propertyTypes: {
        house: 'Casa',
        apartment: 'Apartamento',
        condo: 'Condomínio',
        villa: 'Vila',
        townhouse: 'Sobrado',
        cabin: 'Cabana',
        cottage: 'Chalé',
        studio: 'Estúdio'
      },
      parking: 'Estacionamento',
      airConditioning: 'Ar-Condicionado',
      kitchen: 'Cozinha Completa',
      washer: 'Máquina de Lavar',
      dryer: 'Secadora',
      tv: 'TV',
      gym: 'Academia',
      hotTub: 'Jacuzzi',
      bbqGrill: 'Churrasqueira',
      gameRoom: 'Sala de Jogos',
      petFriendly: 'Pet-Friendly',
      wheelchairAccessible: 'Acessível para Cadeirantes',
      evCharger: 'Carregador de Carro Elétrico',
      linkCopied: 'Link copiado!',
      addedToFavorites: 'Adicionado aos favoritos',
      removedFromFavorites: 'Removido dos favoritos',
      loginToContact: 'Faça login para contatar',
      messageSent: 'Mensagem enviada com sucesso!',
      backToSearch: 'Voltar',
      previousPage: 'Voltar',
      homePage: 'Início',
      sponsored: 'Patrocinado',
      verified: 'Verificado',
      aboutProperty: 'Sobre este imóvel',
      noDescription: 'Nenhuma descrição disponível',
      yes: 'Sim',
      propertyDetails: 'Detalhes do Imóvel',
      type: 'Tipo',
      area: 'Área',
      distanceDisney: 'Distância Disney',
      distanceUniversal: 'Distância Universal',
      houseRules: 'Regras da Casa',
      noSmoking: 'Não Fumar',
      noEvents: 'Sem Eventos',
      noSpecialRules: 'Sem regras especiais adicionais.',
      childrenAllowed: 'Crianças Permitidas',
      minNights: 'Mínimo {n} noites',
      cancellationPolicy: 'Política de Cancelamento',
      flexible: 'Amena',
      moderate: 'Moderada',
      strict: 'Rígida',
      rate: 'Avaliar',
      loadingReviews: 'Carregando avaliações...',
      hostsCannotReview: 'Anfitriões não podem avaliar imóveis.',
      loginToReviewProperty: 'Faça login para avaliar este imóvel',
      login: 'Fazer Login',
      cleaningFee: 'Taxa de limpeza',
      securityDeposit: 'Depósito de segurança',
      contactDirectly: 'Você entrará em contato direto com o anfitrião',
      host: 'Anfitrião',
      reviewsCount: 'avaliações',
      whatsappNotAvailable: 'Telefone WhatsApp não disponível',
      emailNotAvailable: 'Email não disponível',
      sendingToHost: 'Enviando seus dados para o anunciante...',
      emailSentSuccess: 'Email enviado! O anunciante receberá seus dados de contato.',
      emailSendError: 'Erro ao enviar email. Tente novamente.',
      openingWhatsapp: 'Abrindo WhatsApp...',
      reviewPublished: 'Avaliação publicada com sucesso!',
      reviewError: 'Erro ao publicar avaliação',
      replyPublished: 'Resposta publicada!',
      replyError: 'Erro ao publicar resposta',
      shareSuccess: 'Compartilhado com sucesso!',
    },
    en: {
      errorLoading: 'Error loading property',
      notFound: 'Property not found',
      bedrooms: 'Bedrooms',
      bathrooms: 'Bathrooms',
      guests: 'Guests',
      perNight: '/night',
      perNightSuggested: 'per night (suggested)',
      description: 'Description',
      amenities: 'Amenities',
      location: 'Location',
      reviews: 'Reviews',
      contactHost: 'Contact Host',
      sendEmail: 'Send Email',
      whatsapp: 'WhatsApp',
      share: 'Share',
      shareWhatsApp: 'Share on WhatsApp',
      shareInstagram: 'Copy for Instagram',
      shareFacebook: 'Share on Facebook',
      shareTwitter: 'Share on Twitter',
      shareCopied: 'Link copied for sharing!',
      addToFavorites: 'Add to Favorites',
      removeFromFavorites: 'Remove from Favorites',
      writeReview: 'Write Review',
      loginToReview: 'Login to review',
      noReviews: 'No reviews yet',
      beFirst: 'Be the first to review!',
      hostLinks: 'Host Links',
      website: 'Website',
      pool: 'Pool',
      wifi: 'Wi-Fi',
      // R1-4: Property type translations (keep English as-is, just for consistency)
      propertyTypes: {
        house: 'House',
        apartment: 'Apartment',
        condo: 'Condo',
        villa: 'Villa',
        townhouse: 'Townhouse',
        cabin: 'Cabin',
        cottage: 'Cottage',
        studio: 'Studio'
      },
      parking: 'Parking',
      airConditioning: 'Air Conditioning',
      kitchen: 'Full Kitchen',
      washer: 'Washing Machine',
      dryer: 'Dryer',
      tv: 'TV',
      gym: 'Gym',
      hotTub: 'Hot Tub',
      bbqGrill: 'BBQ Grill',
      gameRoom: 'Game Room',
      petFriendly: 'Pet-Friendly',
      wheelchairAccessible: 'Wheelchair Accessible',
      evCharger: 'EV Charger',
      linkCopied: 'Link copied!',
      addedToFavorites: 'Added to favorites',
      removedFromFavorites: 'Removed from favorites',
      loginToContact: 'Login to contact',
      messageSent: 'Message sent successfully!',
      backToSearch: 'Back',
      previousPage: 'Back',
      homePage: 'Home',
      sponsored: 'Sponsored',
      verified: 'Verified',
      aboutProperty: 'About this property',
      noDescription: 'No description available',
      yes: 'Yes',
      propertyDetails: 'Property Details',
      type: 'Type',
      area: 'Area',
      distanceDisney: 'Distance to Disney',
      distanceUniversal: 'Distance to Universal',
      houseRules: 'House Rules',
      noSmoking: 'No Smoking',
      noEvents: 'No Events',
      noSpecialRules: 'No additional special rules.',
      childrenAllowed: 'Children Allowed',
      minNights: 'Minimum {n} nights',
      cancellationPolicy: 'Cancellation Policy',
      flexible: 'Flexible',
      moderate: 'Moderate',
      strict: 'Strict',
      rate: 'Rate',
      loadingReviews: 'Loading reviews...',
      hostsCannotReview: 'Hosts cannot review properties.',
      loginToReviewProperty: 'Login to review this property',
      login: 'Login',
      cleaningFee: 'Cleaning fee',
      securityDeposit: 'Security deposit',
      contactDirectly: 'You will contact the host directly',
      host: 'Host',
      reviewsCount: 'reviews',
      whatsappNotAvailable: 'WhatsApp phone not available',
      emailNotAvailable: 'Email not available',
      sendingToHost: 'Sending your info to the host...',
      emailSentSuccess: 'Email sent! The host will receive your contact info.',
      emailSendError: 'Error sending email. Please try again.',
      openingWhatsapp: 'Opening WhatsApp...',
      reviewPublished: 'Review published successfully!',
      reviewError: 'Error publishing review',
      replyPublished: 'Reply published!',
      replyError: 'Error publishing reply',
      shareSuccess: 'Shared successfully!',
    }
  };
  const txt = texts[language] || texts.en;

  // Ref to prevent duplicate fetches for the same property
  const fetchedPropertyIdRef = useRef(null);

  useEffect(() => {
    // Skip if already fetched for this id (handles React StrictMode)
    if (fetchedPropertyIdRef.current === id) {
      return;
    }
    fetchedPropertyIdRef.current = id;

    fetchProperty();
    fetchReviews();
    checkFavoriteStatus();
    
    // Listen for favorite changes from other components
    const handleFavoritesChanged = () => {
      checkFavoriteStatus();
    };
    
    window.addEventListener('favoritesChanged', handleFavoritesChanged);
    return () => {
      window.removeEventListener('favoritesChanged', handleFavoritesChanged);
    };
  }, [id]);

  const checkFavoriteStatus = async () => {
    const token = localStorage.getItem('magicstay_token');
    if (!token || !id) return;
    
    try {
      // Check from API first
      const response = await api.get(`/favorites/check/${id}`);
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      // Fallback to localStorage (check both keys for backward compatibility)
      const favoritesArray = JSON.parse(localStorage.getItem('favorites') || '[]');
      const favoritesIds = JSON.parse(localStorage.getItem('magicstay_favorites') || '[]');
      setIsFavorite(favoritesArray.some(f => f.id === id) || favoritesIds.includes(id));
    }
  };

  const fetchProperty = async () => {
    try {
      const response = await propertiesAPI.getById(id);
      setProperty(response.data);
      
      // Track property view with thumbnail for recent views display
      analytics.propertyView(id, {
        title: response.data.title,
        city: response.data.city,
        price: response.data.price_per_night || response.data.price || response.data.nightly_price_suggestion || 0,
        plan_type: response.data.plan_type,
        thumbnail: response.data.images?.[0]?.image_url || null
      });
    } catch (error) {
      // R3-3 FIX: Don't log expected errors (404 for missing property)
      if (error.response?.status !== 404) {
        console.error('Error fetching property:', error);
      }
      toast.error(txt.errorLoading);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await reviewsAPI.getByProperty(id);
      // R1-1 FIX: Store full response for average_rating
      setReviewsData(response.data);
      // Handle both array and object responses
      const reviewsList = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.reviews || []);
      setReviews(reviewsList);
    } catch (error) {
      // R3-3 FIX: Don't log expected errors (404 for property without reviews)
      if (error.response?.status !== 404 && error.response?.status !== 401) {
        console.error('Error fetching reviews:', error);
      }
      setReviews([]); // Ensure reviews is always an array
      setReviewsData(null);
    } finally {
      setReviewsLoading(false);
    }
  };

  // State for pre-loaded contact info
  const [contactInfo, setContactInfo] = useState(null);
  const contactFetchedRef = useRef(false);
  const isLoggedIn = !!user;

  // Pre-fetch contact info when user is logged in - fetch only once per property
  useEffect(() => {
    // Reset fetch flag when property ID changes
    contactFetchedRef.current = false;
  }, [id]);

  useEffect(() => {
    const fetchContactInfo = async () => {
      // Skip if not logged in, no id, or already fetched
      if (!isLoggedIn || !id || contactFetchedRef.current) return;
      
      // Mark as fetched immediately to prevent duplicate calls (also handles StrictMode)
      contactFetchedRef.current = true;
      
      try {
        const response = await api.get(`/properties/${id}/contact`);
        setContactInfo(response.data);
        console.log('Contact info pre-loaded:', response.data);
      } catch (error) {
        console.log('Could not pre-load contact info:', error);
        // Reset flag on error so it can retry on next render if needed
        contactFetchedRef.current = false;
      }
    };
    fetchContactInfo();
  }, [isLoggedIn, id]);

  const handleContact = (method) => {
    if (!user) {
      toast.error(txt.loginToContact);
      navigate('/login');
      return;
    }

    // Use pre-loaded contact info
    if (!contactInfo) {
      toast.info(language === 'pt' ? 'Carregando contato, tente novamente...' : 'Loading contact, please try again...');
      return;
    }

    const contactEmail = contactInfo?.contact_email || '';
    const contactPhone = contactInfo?.contact_phone || '';
    const countryCode = contactInfo?.country_code || '+55';

    console.log('Using pre-loaded contact:', { contactEmail, contactPhone, countryCode });

    if (method === 'whatsapp') {
      if (!contactPhone) {
        toast.error(txt.whatsappNotAvailable);
        return;
      }
      
      const cleanPhone = contactPhone.replace(/\D/g, '');
      const cleanCountryCode = countryCode.replace(/\D/g, '');
      const fullPhone = `${cleanCountryCode}${cleanPhone}`;
      const message = encodeURIComponent(language === 'en' ? `Hi! I'm interested in the property: ${property.title}` : `Olá! Tenho interesse no imóvel: ${property.title}`);
      const url = `https://wa.me/${fullPhone}?text=${message}`;
      
      console.log('Opening WhatsApp:', url);
      
      // Open WhatsApp immediately (synchronous call from user click - no popup blocker)
      window.open(url, '_blank');
      toast.success(txt.openingWhatsapp);
      
      // Register contact in background
      api.post(`/properties/${id}/contact`, {
        property_id: id,
        contact_type: 'whatsapp'
      }).catch(err => console.log('Contact registration:', err));
      
    } else if (method === 'email') {
      if (!contactEmail) {
        toast.error(txt.emailNotAvailable);
        return;
      }
      
      toast.info(txt.sendingToHost);
      
      api.post(`/properties/${id}/contact`, {
        property_id: id,
        contact_type: 'email'
      }).then(() => {
        toast.success(txt.emailSentSuccess);
      }).catch((error) => {
        console.error('Error sending email:', error);
        toast.error(txt.emailSendError || 'Erro ao enviar email');
      });
    }
  };

  const handleReviewSubmit = async (reviewData) => {
    if (!user) {
      toast.error(txt.loginToReview);
      navigate('/login');
      return;
    }

    try {
      await reviewsAPI.create({
        ...reviewData,
        property_id: id
      });
      toast.success(txt.reviewPublished);
      setShowReviewForm(false);
      fetchReviews();
      fetchProperty(); // Refresh to update ratings
    } catch (error) {
      console.error('Review error:', error);
      toast.error(error.response?.data?.detail || txt.reviewError);
    }
  };

  const handleReplySubmit = async (reviewId, replyText) => {
    try {
      await reviewsAPI.createReply(reviewId, { reply_text: replyText });
      toast.success(txt.replyPublished);
      fetchReviews();
    } catch (error) {
      console.error('Reply error:', error);
      toast.error(error.response?.data?.detail || txt.replyError);
      throw error;
    }
  };

  const handleFavorite = async () => {
    const token = localStorage.getItem('magicstay_token');
    if (!token) {
      toast.error(txt.loginToContact);
      navigate('/login');
      return;
    }
    
    setIsFavoriteLoading(true);
    
    try {
      if (isFavorite) {
        // Remove from favorites via API
        await api.delete(`/favorites/${id}`);
        setIsFavorite(false);
        toast.success(txt.removedFromFavorites);
        
        // Also update localStorage for sync
        const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
        const idx = favorites.findIndex(f => f.id === id);
        if (idx > -1) {
          favorites.splice(idx, 1);
          localStorage.setItem('favorites', JSON.stringify(favorites));
        }
        
        // Also update legacy localStorage key
        const favoritesIds = JSON.parse(localStorage.getItem('magicstay_favorites') || '[]');
        const idIdx = favoritesIds.indexOf(id);
        if (idIdx > -1) {
          favoritesIds.splice(idIdx, 1);
          localStorage.setItem('magicstay_favorites', JSON.stringify(favoritesIds));
        }
      } else {
        // Add to favorites via API
        await api.post(`/favorites/${id}`);
        setIsFavorite(true);
        toast.success(txt.addedToFavorites);
        
        // Also update localStorage for sync
        const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
        if (!favorites.some(f => f.id === id)) {
          favorites.push(property);
          localStorage.setItem('favorites', JSON.stringify(favorites));
        }
        
        // Also update legacy localStorage key
        const favoritesIds = JSON.parse(localStorage.getItem('magicstay_favorites') || '[]');
        if (!favoritesIds.includes(id)) {
          favoritesIds.push(id);
          localStorage.setItem('magicstay_favorites', JSON.stringify(favoritesIds));
        }
      }
      
      // Dispatch event to sync with other components
      window.dispatchEvent(new CustomEvent('favoritesChanged'));
    } catch (error) {
      console.error('Favorite error:', error);
      // Fallback to localStorage if API fails
      const favoritesIds = JSON.parse(localStorage.getItem('magicstay_favorites') || '[]');
      const index = favoritesIds.indexOf(id);
      
      if (index > -1) {
        favoritesIds.splice(index, 1);
        setIsFavorite(false);
        toast.success(txt.removedFromFavorites);
      } else {
        favoritesIds.push(id);
        setIsFavorite(true);
        toast.success(txt.addedToFavorites);
      }
      
      localStorage.setItem('magicstay_favorites', JSON.stringify(favoritesIds));
      window.dispatchEvent(new CustomEvent('favoritesChanged'));
    } finally {
      setIsFavoriteLoading(false);
    }
  };

  // Share on WhatsApp
  const handleShareWhatsApp = () => {
    const shareUrl = window.location.href;
    const displayPrice = property?.price_per_night || property?.price || property?.nightly_price_suggestion || 0;
    const text = language === 'en' 
      ? `Check out this amazing property: ${property?.title} in ${property?.city}, ${property?.state} - $${displayPrice}/night`
      : `Confira este imóvel incrível: ${property?.title} em ${property?.city}, ${property?.state} - $${displayPrice}/noite`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + '\n' + shareUrl)}`;
    window.open(whatsappUrl, '_blank');
    toast.success(txt.shareSuccess);
  };

  // Share on Facebook
  const handleShareFacebook = () => {
    const shareUrl = window.location.href;
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
    toast.success(txt.shareSuccess);
  };

  // Share on Twitter/X
  const handleShareTwitter = () => {
    const shareUrl = window.location.href;
    const text = language === 'en' 
      ? `Check out this amazing property in ${property?.city}!`
      : `Confira este imóvel incrível em ${property?.city}!`;
    
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
    toast.success(txt.shareSuccess);
  };

  // Copy link for Instagram (Instagram doesn't support direct sharing)
  const handleShareInstagram = async () => {
    const shareUrl = window.location.href;
    const displayPrice = property?.price_per_night || property?.price || property?.nightly_price_suggestion || 0;
    const text = language === 'en' 
      ? `🏠 ${property?.title}\n📍 ${property?.city}, ${property?.state}\n💰 $${displayPrice}/night\n\n🔗 ${shareUrl}`
      : `🏠 ${property?.title}\n📍 ${property?.city}, ${property?.state}\n💰 $${displayPrice}/noite\n\n🔗 ${shareUrl}`;
    
    try {
      await navigator.clipboard.writeText(text);
      toast.success(txt.shareCopied + ' ' + (language === 'en' ? 'Paste it on Instagram!' : 'Cole no Instagram!'));
    } catch (error) {
      toast.info(text, { duration: 10000 });
    }
  };

  const handleShare = async () => {
    
    const shareUrl = window.location.href;
    const shareData = {
      title: property?.title || (language === 'en' ? 'Amazing property' : 'Imóvel incrível'),
      text: language === 'en' ? `Check out this amazing property in ${property?.city}, ${property?.state}` : `Confira este imóvel incrível em ${property?.city}, ${property?.state}`,
      url: shareUrl
    };
    
    try {
      // Try Web Share API first (works on mobile)
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success(txt.shareSuccess);
        return;
      }
      
      // Fallback 1: Try clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(txt.linkCopied);
        return;
      }
      
      // Fallback 2: Use older execCommand method
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success(txt.linkCopied);
      } else {
        throw new Error('execCommand failed');
      }
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('❌ Erro ao compartilhar:', error);
        // Last resort: show the URL in a toast
        toast.info(`Link: ${shareUrl}`, { duration: 10000 });
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <PropertyDetailSkeleton />
        <Footer />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        <Navbar />
        <div className="flex-1 container mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto text-center mb-12"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Home className="w-12 h-12 text-[#00C2D1]" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{txt.notFound}</h2>
            <p className="text-gray-500 mb-6">
              {language === 'pt' 
                ? 'O imóvel que você está procurando não está mais disponível ou foi removido.'
                : 'The property you are looking for is no longer available or has been removed.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={() => navigate('/search')}
                className="bg-[#00C2D1] hover:bg-[#00A8B5] text-white"
              >
                <Search className="w-4 h-4 mr-2" />
                {txt.backToSearch}
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
              >
                {language === 'pt' ? 'Voltar para Home' : 'Back to Home'}
              </Button>
            </div>
          </motion.div>
          
          {/* Similar Properties Suggestions */}
          <div className="mt-8">
            <SimilarProperties 
              currentProperty={{ city: 'Orlando', bedrooms: 3 }} 
              language={language}
              maxItems={4}
            />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const isSuperHost = property.host?.super_host || false;
  const hostRating = property.host?.average_rating;
  const hostReviewCount = property.host?.review_count || 0;
  const allowWhatsApp = ['diamond', 'premium', 'gold'].includes(property.plan_type);
  
  // R1-1 FIX: Use average_rating from API response, fallback to calculating from reviews
  // Also use 'rating' field (not 'rating_overall') from individual reviews
  // Only show rating if there are actual reviews
  const averageRating = reviews.length > 0 
    ? (reviewsData?.average_rating || (reviews.reduce((acc, r) => acc + (Number(r.rating) || 0), 0) / reviews.length).toFixed(1)) 
    : null;
  const hasReviews = reviews.length > 0;

  return (
    <div className="min-h-screen flex flex-col bg-white" style={{ transform: 'translateZ(0)' }}>
      <Navbar />

      {/* R1-2 FIX: Single navigation bar - removed duplicate NavigationButtons below */}
      <div className="bg-background border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.history.back()}
              className="gap-2"
              data-testid="back-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12 19-7-7 7-7"/>
                <path d="M19 12H5"/>
              </svg>
              {txt.previousPage}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="gap-2"
              data-testid="home-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              {txt.homePage}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 max-w-7xl" data-testid="property-detail">
          {/* Title and Actions */}
          <div className="mb-8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="flex-1">
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold font-['Montserrat'] text-[#08172F] mb-3 leading-[1.05] tracking-tight">
                  {titleLoading ? (
                    <span className="animate-pulse bg-gray-200 rounded inline-block w-3/4 h-10"></span>
                  ) : translatedTitle || property.title}
                </h1>
                <div className="flex items-center gap-3 flex-wrap">
                  {property.is_sponsored && (
                    <Badge variant="outline" className="bg-gradient-to-r from-[#FFE9A3]/20 to-[#FFC857]/20 text-[#0B1220] border-[#FFC857]/50 font-medium">
                      {txt.sponsored}
                    </Badge>
                  )}
                  {isSuperHost && (
                    <Badge variant="outline" className="bg-gradient-to-r from-[#FFF3D4] to-[#FFE9A3] text-[#9A7100] border-[#9A7100]/30 font-medium">
                      Super Host
                    </Badge>
                  )}
                  {property.is_verified && (
                    <Badge variant="outline" className="bg-gradient-to-r from-[#E6FBFF] to-[#D4F5F8] text-[#006E77] border-[#00C2D1]/30 font-medium">
                      {txt.verified}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Share Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      type="button"
                      variant="outline" 
                      size="icon"
                      data-testid="share-button-detail"
                      aria-label={txt.share}
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={handleShareWhatsApp} className="cursor-pointer">
                      <svg className="w-4 h-4 mr-2 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      <span>WhatsApp</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShareInstagram} className="cursor-pointer">
                      <Instagram className="w-4 h-4 mr-2 text-pink-500" />
                      <span>Instagram</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShareFacebook} className="cursor-pointer">
                      <Facebook className="w-4 h-4 mr-2 text-blue-600" />
                      <span>Facebook</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShareTwitter} className="cursor-pointer">
                      <Twitter className="w-4 h-4 mr-2 text-sky-500" />
                      <span>Twitter / X</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShare} className="cursor-pointer">
                      <Copy className="w-4 h-4 mr-2 text-gray-500" />
                      <span>{txt.linkCopied?.replace('!', '') || 'Copy Link'}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <Button 
                  type="button"
                  variant="outline" 
                  size="icon"
                  onClick={handleFavorite}
                  disabled={isFavoriteLoading}
                  data-testid="favorite-button-detail"
                  aria-label={isFavorite ? t('accessibility.removeFromFavorites') : t('accessibility.addToFavorites')}
                >
                  {isFavoriteLoading ? (
                    <span className="w-4 h-4 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
                  ) : (
                    <Heart className={`w-4 h-4 transition-colors ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                  )}
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm">
              {reviews.length > 0 && (
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="font-semibold">{averageRating}</span>
                  <span className="text-gray-600">({reviews.length} {txt.reviewsCount})</span>
                </div>
              )}
              <div className="flex items-center gap-1 text-gray-600">
                <MapPin className="w-4 h-4" />
                <span>{property.city}, {property.state}</span>
              </div>
            </div>
          </div>

          {/* Photo Gallery - Premium */}
          <div className="mb-8">
            <PhotoGallery 
              images={property.images} 
              title={translatedTitle || property.title} 
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* Left Column - Details */}
            <div className="md:col-span-2 space-y-8">
              {/* Property Info */}
              <div>
                <h2 className="text-2xl font-bold font-['Montserrat'] mb-4 text-gray-900">{txt.aboutProperty}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-gray-500" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{property.max_guests}</div>
                      <div className="text-xs text-gray-600">{txt.guests}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Bed className="w-5 h-5 text-gray-500" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{property.bedrooms}</div>
                      <div className="text-xs text-gray-600">{txt.bedrooms}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Bath className="w-5 h-5 text-gray-500" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{property.bathrooms}</div>
                      <div className="text-xs text-gray-600">{txt.bathrooms}</div>
                    </div>
                  </div>
                  {property.has_pool && (
                    <div className="flex items-center gap-2">
                      <Waves className="w-5 h-5 text-gray-500" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{txt.yes}</div>
                        <div className="text-xs text-gray-600">{txt.pool}</div>
                      </div>
                    </div>
                  )}
                </div>
                <Separator className="my-6" />
                {descLoading ? (
                  <div className="space-y-2">
                    <span className="animate-pulse bg-gray-200 rounded block w-full h-4"></span>
                    <span className="animate-pulse bg-gray-200 rounded block w-full h-4"></span>
                    <span className="animate-pulse bg-gray-200 rounded block w-3/4 h-4"></span>
                  </div>
                ) : (
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {translatedDescription || property.description || txt.noDescription}
                  </p>
                )}
              </div>

              {/* Property Details */}
              <div>
                <h3 className="text-xl font-bold font-['Montserrat'] mb-4 text-gray-900">{txt.propertyDetails}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                  {property.property_type && (
                    <div className="text-sm">
                      <div className="text-gray-600">{txt.type}</div>
                      {/* R1-4 FIX: Translate property_type */}
                      <div className="font-medium text-gray-900 capitalize">
                        {txt.propertyTypes?.[property.property_type] || property.property_type}
                      </div>
                    </div>
                  )}
                  {property.square_feet && (
                    <div className="text-sm">
                      <div className="text-gray-600">{txt.area}</div>
                      <div className="font-medium text-gray-900">{property.square_feet} ft²</div>
                    </div>
                  )}
                  {property.distance_to_disney_miles && (
                    <div className="text-sm">
                      <div className="text-gray-600">{txt.distanceDisney}</div>
                      <div className="font-medium text-gray-900">{property.distance_to_disney_miles} mi</div>
                    </div>
                  )}
                  {property.distance_to_universal_miles && (
                    <div className="text-sm">
                      <div className="text-gray-600">{txt.distanceUniversal}</div>
                      <div className="font-medium text-gray-900">{property.distance_to_universal_miles} mi</div>
                    </div>
                  )}
                  <div className="text-sm">
                    <div className="text-gray-600">Check-in</div>
                    <div className="font-medium text-gray-900">{property.check_in_time || '15:00'}</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-gray-600">Check-out</div>
                    <div className="font-medium text-gray-900">{property.check_out_time || '11:00'}</div>
                  </div>
                </div>
              </div>

              {/* Amenities - With Visual Icons */}
              <div>
                <h3 className="text-xl font-bold font-['Montserrat'] mb-4 text-gray-900">{txt.amenities}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {property.has_wifi && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <Wifi className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">Wi-Fi</span>
                    </div>
                  )}
                  {property.has_parking && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <Car className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{txt.parking}</span>
                    </div>
                  )}
                  {property.has_ac && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <Snowflake className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{txt.airConditioning}</span>
                    </div>
                  )}
                  {property.has_kitchen && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <ChefHat className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{txt.kitchen}</span>
                    </div>
                  )}
                  {property.has_pool && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <Waves className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{txt.pool}</span>
                    </div>
                  )}
                  {property.has_tv && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <Tv className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">TV</span>
                    </div>
                  )}
                  {property.has_washer && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <WashingMachine className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{txt.washer}</span>
                    </div>
                  )}
                  {property.has_dryer && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <Wind className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{txt.dryer}</span>
                    </div>
                  )}
                  {property.has_gym && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <Dumbbell className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{txt.gym}</span>
                    </div>
                  )}
                  {property.has_hot_tub && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <Flame className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{txt.hotTub}</span>
                    </div>
                  )}
                  {property.has_bbq_grill && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <UtensilsCrossed className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{txt.bbqGrill}</span>
                    </div>
                  )}
                  {property.has_game_room && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <Gamepad2 className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{txt.gameRoom}</span>
                    </div>
                  )}
                  {property.has_pet_friendly && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <Dog className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">Pet-Friendly</span>
                    </div>
                  )}
                  {property.has_wheelchair_accessible && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                        <Accessibility className="w-5 h-5 text-[#00C2D1]" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{txt.wheelchairAccessible}</span>
                    </div>
                  )}
                  {property.has_ev_charger && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                        <Zap className="w-5 h-5 text-green-500" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{txt.evCharger || 'EV Charger'}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* House Rules */}
              {property.house_rules && (
                <div>
                  <h3 className="text-xl font-bold font-['Montserrat'] mb-4 text-gray-900">{txt.houseRules}</h3>
                  <div className="space-y-2">
                    {/* R1-3 FIX: Filter out rules that are already shown as chips to avoid duplication */}
                    <p className="text-sm text-gray-700">
                      {property.house_rules
                        .split(/[.\n]/)
                        .filter(rule => {
                          const lowerRule = rule.toLowerCase().trim();
                          // Filter out smoking-related text if we show the chip
                          if (!property.smoking_allowed && (
                            lowerRule.includes('fumar') || 
                            lowerRule.includes('smoking') ||
                            lowerRule.includes('não fumar') ||
                            lowerRule.includes('proibido fumar') ||
                            lowerRule.includes('no smoking')
                          )) return false;
                          // Filter out events-related text if we show the chip
                          if (!property.events_allowed && (
                            lowerRule.includes('festas') ||
                            lowerRule.includes('eventos') ||
                            lowerRule.includes('parties') ||
                            lowerRule.includes('events')
                          )) return false;
                          return rule.trim().length > 0;
                        })
                        .join('. ')
                        .trim() || txt.noSpecialRules
                      }
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {!property.smoking_allowed && (
                        <Badge variant="outline" className="text-gray-900 border-gray-400">{txt.noSmoking}</Badge>
                      )}
                      {!property.events_allowed && (
                        <Badge variant="outline" className="text-gray-900 border-gray-400">{txt.noEvents}</Badge>
                      )}
                      {property.children_allowed && (
                        <Badge variant="outline" className="text-gray-900 border-gray-400">{txt.childrenAllowed}</Badge>
                      )}
                      {property.min_nights > 1 && (
                        <Badge variant="outline" className="text-gray-900 border-gray-400">{txt.minNights.replace('{n}', property.min_nights)}</Badge>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Location Map - Enhanced */}
              <PropertyLocationMap 
                property={property}
                language={language}
                height="350px"
              />

              {/* Cancellation Policy */}
              <div>
                <h3 className="text-xl font-bold font-['Montserrat'] mb-4 text-gray-900">{txt.cancellationPolicy}</h3>
                <div className="text-sm text-gray-700">
                  {property.cancellation_policy === 'flexible' && (
                    <p>{txt.flexible}</p>
                  )}
                  {property.cancellation_policy === 'moderate' && (
                    <p>{txt.moderate}</p>
                  )}
                  {property.cancellation_policy === 'strict' && (
                    <p>{txt.strict}</p>
                  )}
                </div>
              </div>

              {/* Reviews Section */}
              <div>
                <Tabs defaultValue="reviews" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="reviews">{txt.reviews} ({reviews.length})</TabsTrigger>
                    <TabsTrigger value="add-review">{txt.rate}</TabsTrigger>
                  </TabsList>
                  <TabsContent value="reviews" className="mt-6">
                    {reviewsLoading ? (
                      <div className="text-center py-8 text-gray-700">{txt.loadingReviews}</div>
                    ) : (
                      <ReviewsList
                        reviews={reviews}
                        propertyId={id}
                        hostId={property.host_id}
                        currentUser={user}
                        onReplySubmit={handleReplySubmit}
                      />
                    )}
                  </TabsContent>
                  <TabsContent value="add-review" className="mt-6">
                    {user ? (
                      user.role === 'host' ? (
                        <div className="text-center py-12 text-gray-700">
                          <p>{txt.hostsCannotReview}</p>
                        </div>
                      ) : (
                        <AddReviewForm propertyId={id} onSuccess={handleReviewSubmit} />
                      )
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-gray-700 mb-4">{txt.loginToReviewProperty}</p>
                        <Button onClick={() => navigate('/login')}>{txt.login}</Button>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            </div>

            {/* Right Column - Contact Card */}
            <div>
              <Card className="sticky top-24 bg-white border">
                <CardContent className="p-6 space-y-6">
                  <div>
                    <div className="text-3xl font-bold text-gray-900">${property.price_per_night || property.price || property.nightly_price_suggestion || 0}</div>
                    <div className="text-sm text-gray-600">{txt.perNightSuggested}</div>
                  </div>

                  {/* Additional Fees */}
                  {(property.cleaning_fee || property.security_deposit) && (
                    <div className="space-y-2 text-sm">
                      {property.cleaning_fee && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">{txt.cleaningFee}</span>
                          <span className="font-medium text-gray-900">${property.cleaning_fee}</span>
                        </div>
                      )}
                      {property.security_deposit && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">{txt.securityDeposit}</span>
                          <span className="font-medium text-gray-900">${property.security_deposit}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <Separator />

                  <div className="space-y-3">
                    <Button
                      className="w-full bg-primary text-white hover:bg-[#0B2246] flex items-center gap-2"
                      onClick={() => handleContact('email')}
                      data-testid="contact-email-button"
                    >
                      <Mail className="w-4 h-4" />
                      {txt.sendEmail}
                    </Button>

                    {allowWhatsApp && (
                      <Button
                        className="w-full bg-green-600 text-white hover:bg-green-700 flex items-center gap-2"
                        onClick={() => handleContact('whatsapp')}
                        data-testid="contact-whatsapp-button"
                      >
                        <MessageCircle className="w-4 h-4" />
                        WhatsApp
                      </Button>
                    )}
                  </div>

                  <div className="text-xs text-gray-600 text-center">
                    {txt.contactDirectly}
                  </div>

                  {/* Social Links - Only for Gold, Premium and Diamond plans */}
                  {(property.plan_type === 'gold' || property.plan_type === 'premium' || property.plan_type === 'diamond') && 
                   (property.website_url || property.facebook_url || property.instagram_url) && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sm flex items-center gap-2">
                          {txt.hostLinks}
                          <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full">
                            {property.plan_type === 'diamond' ? 'Diamond' : property.plan_type === 'premium' ? 'Premium' : 'Gold'}
                          </span>
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {property.website_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2"
                              onClick={() => window.open(property.website_url, '_blank')}
                              data-testid="social-website-button"
                            >
                              <Globe className="w-4 h-4" />
                              {txt.website}
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                          )}
                          {property.facebook_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2 text-[#1877F2] border-[#1877F2] hover:bg-[#1877F2]/10"
                              onClick={() => window.open(property.facebook_url, '_blank')}
                              data-testid="social-facebook-button"
                            >
                              <Facebook className="w-4 h-4" />
                              Facebook
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                          )}
                          {property.instagram_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2 text-[#E4405F] border-[#E4405F] hover:bg-[#E4405F]/10"
                              onClick={() => window.open(property.instagram_url, '_blank')}
                              data-testid="social-instagram-button"
                            >
                              <Instagram className="w-4 h-4" />
                              Instagram
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {property.host && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="font-semibold mb-2">{txt.host}</h4>
                        <p className="text-sm">{property.host.name}</p>
                        {isSuperHost && (
                          <Badge variant="outline" className="mt-2 bg-[#FFF3D4] text-[#9A7100] border-[#9A7100]/20">
                            Super Host
                          </Badge>
                        )}
                        {hostReviewCount > 0 && hostRating && (
                          <div className="flex items-center gap-1 mt-2 text-sm">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <span className="font-medium">{Number(hostRating).toFixed(1)}</span>
                            <span className="text-gray-600">({hostReviewCount} {txt.reviewsCount})</span>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <BackToTopButton />
    </div>
  );
};

export default PropertyDetail;